import { useLanguage } from '../contexts/LanguageContext'
import { translations } from '../data/translations'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

// Count Up Animation Component
const CountUpAnimation = ({ targetValue, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const duration = 2000
    const steps = 60
    const increment = targetValue / steps
    const stepDuration = duration / steps

    let currentStep = 0
    const timer = setInterval(() => {
      currentStep++
      const newValue = Math.min(Math.floor(increment * currentStep), targetValue)
      setCount(newValue)

      if (currentStep >= steps) {
        clearInterval(timer)
        setCount(targetValue)
      }
    }, stepDuration)

    return () => clearInterval(timer)
  }, [targetValue])

  return <span>{prefix}{count}{suffix}</span>
}

const About = () => {
  const { language } = useLanguage()
  const t = translations[language]

  const stats = [
    {
      value: 10000,
      suffix: '+',
      label: language === 'en' ? 'Deliveries Completed' : 'توصيلات مكتملة',
      icon: '📦',
    },
    {
      value: 500,
      suffix: '+',
      label: language === 'en' ? 'Active Clients' : 'عملاء نشطون',
      icon: '🏢',
    },
    {
      value: 98,
      suffix: '%',
      label: language === 'en' ? 'Success Rate' : 'معدل النجاح',
      icon: '✓',
    },
    {
      value: 24,
      prefix: '',
      suffix: '/7',
      label: language === 'en' ? 'Support' : 'دعم',
      icon: '📞',
    },
  ]

  const values = [
    {
      title: language === 'en' ? 'Reliability' : 'الموثوقية',
      description: language === 'en'
        ? 'We deliver on our promises, ensuring your packages arrive safely and on time, every single time.'
        : 'نحن نفي بوعودنا، ونتأكد من وصول طرودك بأمان وفي الوقت المحدد، في كل مرة.',
      icon: '✓',
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: language === 'en' ? 'Transparency' : 'الشفافية',
      description: language === 'en'
        ? 'Real-time tracking and clear communication at every step of the delivery process, keeping you informed always.'
        : 'تتبع في الوقت الفعلي وتواصل واضح في كل خطوة من عملية التوصيل، مما يبقيك على اطلاع دائمًا.',
      icon: '👁️',
      color: 'from-green-500 to-green-600',
    },
    {
      title: language === 'en' ? 'Innovation' : 'الابتكار',
      description: language === 'en'
        ? 'Cutting-edge technology and continuous improvement to serve you better with the latest delivery solutions.'
        : 'تكنولوجيا متطورة وتحسين مستمر لخدمتك بشكل أفضل بأحدث حلول التوصيل.',
      icon: '💡',
      color: 'from-yellow-500 to-yellow-600',
    },
    {
      title: language === 'en' ? 'Customer Focus' : 'التركيز على العملاء',
      description: language === 'en'
        ? 'Your satisfaction is our priority. We go the extra mile for every customer, building lasting relationships.'
        : 'رضاك هو أولويتنا. نبذل قصارى جهدنا لكل عميل، وبناء علاقات دائمة.',
      icon: '❤️',
      color: 'from-red-500 to-red-600',
    },
  ]

  const whySmartMile = [
    {
      title: language === 'en' ? 'Advanced Technology' : 'تكنولوجيا متقدمة',
      description: language === 'en'
        ? 'State-of-the-art tracking systems and automated dispatch for seamless operations'
        : 'أنظمة تتبع متطورة وإرسال تلقائي للعمليات السلسة',
      icon: '🚀',
    },
    {
      title: language === 'en' ? 'Nationwide Coverage' : 'تغطية شاملة',
      description: language === 'en'
        ? 'Serving all seven emirates with a comprehensive delivery network'
        : 'خدمة جميع الإمارات السبع بشبكة توصيل شاملة',
      icon: '🗺️',
    },
    {
      title: language === 'en' ? 'Expert Team' : 'فريق خبير',
      description: language === 'en'
        ? 'Trained professionals dedicated to delivering excellence in every shipment'
        : 'محترفون مدربون ملتزمون بتقديم التميز في كل شحنة',
      icon: '👥',
    },
    {
      title: language === 'en' ? 'Flexible Solutions' : 'حلول مرنة',
      description: language === 'en'
        ? 'Customized delivery options tailored to your specific business needs'
        : 'خيارات توصيل مخصصة مصممة خصيصًا لاحتياجات عملك',
      icon: '⚙️',
    },
  ]

  const advantages = [
    {
      title: language === 'en' ? 'Fast Delivery' : 'توصيل سريع',
      description: language === 'en'
        ? 'Our optimized delivery network ensures same-day delivery within major cities and express options for urgent shipments. With strategically located distribution centers and AI-powered route optimization, we guarantee faster transit times. Standard deliveries are completed within 24-48 hours, while express options can reach destinations in as little as 2-4 hours for local deliveries.'
        : 'تضمن شبكة التوصيل المحسّنة لدينا التوصيل في نفس اليوم داخل المدن الكبرى وخيارات التوصيل السريع للشحنات العاجلة. مع مراكز التوزيع الموزعة استراتيجيًا وتحسين المسار المدعوم بالذكاء الاصطناعي، نضمن أوقات عبور أسرع. يتم إكمال التوصيلات القياسية خلال 24-48 ساعة، بينما يمكن أن تصل خيارات التوصيل السريع إلى الوجهات في أقل من 2-4 ساعات للتوصيلات المحلية.',
      icon: '⚡',
    },
    {
      title: language === 'en' ? 'Secure Handling' : 'معالجة آمنة',
      description: language === 'en'
        ? 'Every package is handled with utmost care by our trained professional riders. We use tamper-proof packaging materials and provide insurance coverage options for valuable items up to AED 10,000. Our secure handling protocols include signature verification, photo proof of delivery, and GPS tracking throughout the entire journey. Fragile items receive special protective packaging at no extra cost.'
        : 'يتم التعامل مع كل طرد بأقصى درجات العناية من قبل سائقينا المحترفين المدربين. نستخدم مواد تغليف مقاومة للعبث ونوفر خيارات تغطية تأمينية للعناصر القيمة حتى 10,000 درهم إماراتي. تشمل بروتوكولات المعالجة الآمنة لدينا التحقق من التوقيع وإثبات الصورة للتوصيل وتتبع GPS طوال الرحلة. تحصل العناصر الهشة على تغليف وقائي خاص بدون تكلفة إضافية.',
      icon: '🛡️',
    },
    {
      title: language === 'en' ? 'Cost Effective' : 'اقتصادي',
      description: language === 'en'
        ? 'We offer transparent, competitive pricing with no hidden fees or surprise charges. Our flexible pricing model includes per-delivery rates starting from AED 15, monthly subscription packages with up to 30% savings, and enterprise contracts with volume discounts. All quotes are provided upfront with detailed breakdowns. We help businesses reduce delivery costs by up to 40% compared to traditional courier services while maintaining premium quality.'
        : 'نقدم أسعارًا شفافة وتنافسية بدون رسوم مخفية أو رسوم مفاجئة. يشمل نموذج التسعير المرن لدينا أسعار لكل توصيل تبدأ من 15 درهم إماراتي وحزم اشتراك شهرية مع توفير يصل إلى 30% وعقود مؤسسية مع خصومات على الحجم. يتم تقديم جميع العروض مسبقًا مع تفاصيل مفصلة. نساعد الشركات على تقليل تكاليف التوصيل بنسبة تصل إلى 40% مقارنة بخدمات البريد السريع التقليدية مع الحفاظ على الجودة المتميزة.',
      icon: '💰',
    },
    {
      title: language === 'en' ? 'Easy Integration' : 'تكامل سهل',
      description: language === 'en'
        ? 'Our RESTful API is developer-friendly and can be integrated with any e-commerce platform, ERP system, or custom application within hours. We provide comprehensive documentation, SDKs for popular programming languages, webhooks for real-time updates, and dedicated technical support. Popular integrations include Shopify, WooCommerce, Magento, and custom solutions. No coding experience required for basic integrations using our pre-built plugins.'
        : 'API الخاص بنا سهل الاستخدام للمطورين ويمكن دمجه مع أي منصة تجارة إلكترونية أو نظام ERP أو تطبيق مخصص في غضون ساعات. نقدم وثائق شاملة وSDKs للغات البرمجة الشائعة وwebhooks للتحديثات في الوقت الفعلي ودعم فني مخصص. تشمل التكاملات الشائعة Shopify وWooCommerce وMagento والحلول المخصصة. لا حاجة لخبرة برمجية للتكاملات الأساسية باستخدام الإضافات الجاهزة لدينا.',
      icon: '🔌',
    },
    {
      title: language === 'en' ? 'Real-Time Tracking' : 'تتبع في الوقت الفعلي',
      description: language === 'en'
        ? 'Every shipment receives a unique tracking ID and live tracking link that customers can access instantly. Our advanced GPS tracking system provides real-time location updates, estimated delivery times, and delivery status notifications via SMS and email. Customers can track their packages on our website, mobile app, or through WhatsApp. Delivery proofs including photos and signatures are automatically uploaded and accessible through the tracking portal.'
        : 'تحصل كل شحنة على معرف تتبع فريد ورابط تتبع مباشر يمكن للعملاء الوصول إليه على الفور. يوفر نظام تتبع GPS المتقدم لدينا تحديثات الموقع في الوقت الفعلي وأوقات التوصيل المقدرة وإشعارات حالة التوصيل عبر SMS والبريد الإلكتروني. يمكن للعملاء تتبع طرودهم على موقعنا الإلكتروني أو التطبيق المحمول أو عبر واتساب. يتم تحميل أدلة التوصيل بما في ذلك الصور والتوقيعات تلقائيًا ويمكن الوصول إليها من خلال بوابة التتبع.',
      icon: '📍',
    },
    {
      title: language === 'en' ? 'Flexible Payment Options' : 'خيارات دفع مرنة',
      description: language === 'en'
        ? 'We accept multiple payment methods including cash on delivery (COD), credit/debit cards, bank transfers, and digital wallets. COD service is available for all deliveries with secure cash collection and prompt transfers to merchants. Our payment gateway supports all major cards and provides instant payment confirmations. Enterprise clients can benefit from net payment terms and automated invoicing. All transactions are secure and PCI-DSS compliant.'
        : 'نقبل طرق دفع متعددة بما في ذلك الدفع نقدًا عند التسليم (COD) وبطاقات الائتمان/الخصم والتحويلات البنكية والمحافظ الرقمية. خدمة COD متاحة لجميع التوصيلات مع تحصيل نقدي آمن وتحويلات فورية للتجار. تدعم بوابة الدفع لدينا جميع البطاقات الرئيسية وتوفر تأكيدات الدفع الفورية. يمكن لعملاء المؤسسات الاستفادة من شروط الدفع الصافية والفوترة الآلية. جميع المعاملات آمنة ومتوافقة مع PCI-DSS.',
      icon: '💳',
    },
    {
      title: language === 'en' ? 'Expert Team' : 'فريق خبير',
      description: language === 'en'
        ? 'Our team consists of experienced logistics professionals, certified riders, and dedicated customer service representatives. All riders undergo comprehensive training in safe driving, package handling, customer service, and route optimization. Our customer support team is available 24/7 and speaks multiple languages including English, Arabic, Hindi, and Urdu. We conduct regular performance reviews and continuous training programs to maintain the highest service standards.'
        : 'يتكون فريقنا من محترفي الخدمات اللوجستية ذوي الخبرة والسائقين المعتمدين وممثلي خدمة العملاء المخصصين. يخضع جميع السائقين لتدريب شامل في القيادة الآمنة والتعامل مع الطرود وخدمة العملاء وتحسين المسار. فريق دعم العملاء لدينا متاح على مدار الساعة ويتحدث لغات متعددة بما في ذلك الإنجليزية والعربية والهندية والأردية. نجري مراجعات أداء منتظمة وبرامج تدريب مستمرة للحفاظ على أعلى معايير الخدمة.',
      icon: '👥',
    },
    {
      title: language === 'en' ? 'Eco-Friendly Practices' : 'ممارسات صديقة للبيئة',
      description: language === 'en'
        ? 'We are committed to environmental sustainability through eco-friendly delivery practices. Our fleet includes electric and hybrid vehicles, and we use recyclable packaging materials whenever possible. We optimize routes to reduce fuel consumption and carbon emissions. Our paperless documentation system minimizes waste, and we partner with recycling programs. We aim to achieve carbon-neutral deliveries by 2025 through continuous improvement of our green initiatives.'
        : 'نحن ملتزمون بالاستدامة البيئية من خلال ممارسات التوصيل الصديقة للبيئة. يشمل أسطولنا مركبات كهربائية وهجينة، ونستخدم مواد تغليف قابلة لإعادة التدوير كلما أمكن ذلك. نحسن المسارات لتقليل استهلاك الوقود وانبعاثات الكربون. يقلل نظام التوثيق الخالي من الورق لدينا من النفايات، ونتعاون مع برامج إعادة التدوير. نسعى لتحقيق توصيلات محايدة للكربون بحلول عام 2025 من خلال التحسين المستمر لمبادراتنا الخضراء.',
      icon: '🌱',
    },
  ]

  const whyChooseUs = [
    {
      title: language === 'en' ? 'Proven Track Record' : 'سجل حافل',
      description: language === 'en'
        ? 'With years of successful operations, we have completed over 10,000+ deliveries across all seven emirates. Our commitment to excellence has earned us the trust of hundreds of businesses, from small startups to large enterprises. We maintain a 98% success rate and continuously improve our services based on customer feedback.'
        : 'مع سنوات من العمليات الناجحة، أكملنا أكثر من 10,000+ توصيل عبر جميع الإمارات السبع. التزامنا بالتميز أكسبنا ثقة مئات الشركات، من الشركات الناشئة الصغيرة إلى المؤسسات الكبيرة. نحافظ على معدل نجاح 98% ونحسن خدماتنا باستمرار بناءً على ملاحظات العملاء.',
      icon: '📊',
    },
    {
      title: language === 'en' ? 'Comprehensive Support' : 'دعم شامل',
      description: language === 'en'
        ? 'Our dedicated 24/7 customer support team is always ready to assist you with any queries, concerns, or delivery issues. We offer multiple communication channels including phone, WhatsApp, email, and live chat. Our support specialists are trained to provide quick resolutions and ensure your delivery experience is seamless from start to finish.'
        : 'فريق دعم العملاء المخصص لدينا على مدار الساعة جاهز دائمًا لمساعدتك في أي استفسارات أو مخاوف أو مشاكل التوصيل. نقدم قنوات اتصال متعددة تشمل الهاتف وواتساب والبريد الإلكتروني والدردشة المباشرة. متخصصو الدعم لدينا مدربون لتقديم حلول سريعة وضمان تجربة توصيل سلسة من البداية إلى النهاية.',
      icon: '📞',
    },
    {
      title: language === 'en' ? 'Scalable Solutions' : 'حلول قابلة للتوسع',
      description: language === 'en'
        ? 'Whether you\'re a startup handling 10 deliveries a day or an enterprise managing thousands, our flexible infrastructure scales seamlessly with your business growth. We offer customizable delivery plans, volume discounts, and dedicated account managers for enterprise clients. Our API integration allows seamless connection with your existing systems.'
        : 'سواء كنت شركة ناشئة تتعامل مع 10 توصيلات في اليوم أو مؤسسة تدير آلاف التوصيلات، فإن بنيتنا التحتية المرنة تتوسع بسلاسة مع نمو عملك. نقدم خطط توصيل قابلة للتخصيص وخصومات على الحجم ومديري حسابات مخصصين لعملاء المؤسسات. يسمح تكامل API الخاص بنا بالاتصال السلس مع أنظمتك الحالية.',
      icon: '📈',
    },
    {
      title: language === 'en' ? 'Advanced Technology' : 'تكنولوجيا متقدمة',
      description: language === 'en'
        ? 'We leverage cutting-edge technology including real-time GPS tracking, automated dispatch systems, and AI-powered route optimization. Our mobile app and web platform provide instant updates, delivery proofs, and comprehensive analytics. Every shipment gets a unique tracking link that customers can access anytime, anywhere.'
        : 'نستفيد من التكنولوجيا المتطورة بما في ذلك تتبع GPS في الوقت الفعلي وأنظمة الإرسال التلقائية وتحسين المسار المدعوم بالذكاء الاصطناعي. تطبيقنا المحمول ومنصتنا الإلكترونية توفر تحديثات فورية وأدلة التوصيل وتحليلات شاملة. كل شحنة تحصل على رابط تتبع فريد يمكن للعملاء الوصول إليه في أي وقت وأي مكان.',
      icon: '💻',
    },
    {
      title: language === 'en' ? 'Nationwide Coverage' : 'تغطية شاملة',
      description: language === 'en'
        ? 'Our extensive network covers all seven emirates including Dubai, Abu Dhabi, Sharjah, Ajman, Umm Al Quwain, Ras Al Khaimah, and Fujairah. We have strategically located warehouses and distribution centers to ensure fast and efficient deliveries. Whether it\'s urban areas or remote locations, we reach every corner of the UAE.'
        : 'تغطي شبكتنا الواسعة جميع الإمارات السبع بما في ذلك دبي وأبو ظبي والشارقة وعجمان وأم القيوين ورأس الخيمة والفجيرة. لدينا مستودعات ومراكز توزيع موزعة استراتيجيًا لضمان توصيلات سريعة وفعالة. سواء كانت المناطق الحضرية أو المواقع النائية، نصل إلى كل ركن من أركان الإمارات.',
      icon: '🗺️',
    },
    {
      title: language === 'en' ? 'Competitive Pricing' : 'أسعار تنافسية',
      description: language === 'en'
        ? 'We offer transparent, competitive pricing with no hidden fees. Our flexible pricing model includes per-delivery rates, monthly packages, and enterprise contracts with volume discounts. We provide detailed quotes upfront and ensure you only pay for what you use. Our cost-effective solutions help businesses reduce their delivery expenses while maintaining quality service.'
        : 'نقدم أسعارًا شفافة وتنافسية بدون رسوم مخفية. نموذج التسعير المرن لدينا يشمل أسعار لكل توصيل وحزم شهرية وعقود مؤسسية مع خصومات على الحجم. نقدم عروض أسعار مفصلة مسبقًا ونتأكد من أنك تدفع فقط مقابل ما تستخدمه. تساعد حلولنا الاقتصادية الشركات على تقليل نفقات التوصيل مع الحفاظ على جودة الخدمة.',
      icon: '💰',
    },
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-primary-500 py-8 md:py-12 lg:py-16 overflow-hidden">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-semibold text-white/90 mb-4 uppercase tracking-wider">
              {language === 'en' ? 'About Us' : 'من نحن'}
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {t.about.title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {t.about.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/Package exchange in the city.png" 
                  alt={language === 'en' ? 'Package exchange in the city' : 'تبادل الطرود في المدينة'}
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-500/20 to-transparent"></div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className={`${language === 'ar' ? 'lg:text-right' : 'lg:text-left'} text-center lg:text-left`}
            >
              <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
                {language === 'en' ? 'Our Story' : 'قصتنا'}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {language === 'en' ? 'Building Trust, One Delivery at a Time' : 'بناء الثقة، توصيل واحد في كل مرة'}
              </h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  {language === 'en'
                    ? 'Smart Mile Delivery Services was founded with a vision to revolutionize last-mile delivery across the United Arab Emirates. We recognized the growing need for reliable, transparent, and efficient delivery solutions in the rapidly expanding e-commerce and business sectors.'
                    : 'تأسست خدمات توصيل سمارت مايل برؤية لإحداث ثورة في التوصيل من الباب إلى الباب عبر دولة الإمارات العربية المتحدة. أدركنا الحاجة المتزايدة لحلول توصيل موثوقة وشفافة وفعالة في قطاعات التجارة الإلكترونية والأعمال سريعة التوسع.'}
                </p>
                <p>
                  {language === 'en'
                    ? 'Since our establishment, we have built a strong network of professional riders, implemented cutting-edge tracking technology, and developed partnerships with businesses of all sizes. Our commitment to excellence has made us a trusted partner for restaurants, pharmacies, e-commerce platforms, and corporate offices throughout the UAE.'
                    : 'منذ تأسيسنا، قمنا ببناء شبكة قوية من السائقين المحترفين، وتنفيذ تكنولوجيا تتبع متطورة، وتطوير شراكات مع الشركات من جميع الأحجام. التزامنا بالتميز جعلنا شريكًا موثوقًا للمطاعم والصيدليات ومنصات التجارة الإلكترونية والمكاتب المؤسسية في جميع أنحاء الإمارات.'}
                </p>
                <p>
                  {language === 'en'
                    ? 'Today, we continue to innovate and expand our services, always keeping our customers at the heart of everything we do.'
                    : 'اليوم، نواصل الابتكار وتوسيع خدماتنا، مع الحفاظ دائمًا على عملائنا في قلب كل ما نقوم به.'}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Running Stats Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="text-4xl mb-3">{stat.icon}</div>
                <motion.div
                  className="text-4xl md:text-5xl font-bold text-primary-600 mb-2"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  <CountUpAnimation 
                    targetValue={stat.value} 
                    suffix={stat.suffix} 
                    prefix={stat.prefix || ''}
                  />
                </motion.div>
                <div className="text-lg md:text-xl font-medium text-gray-700">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
              {language === 'en' ? 'Our Values' : 'قيمنا'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'What We Stand For' : 'ما نؤمن به'}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              {language === 'en'
                ? 'The core principles that drive our commitment to excellence'
                : 'المبادئ الأساسية التي تحرك التزامنا بالتميز'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative overflow-hidden bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-transparent rounded-bl-full"></div>
                <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <span className="text-3xl">{value.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission and Vision Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative bg-primary-500/10 backdrop-blur-md rounded-2xl p-8 border border-primary-200 hover:bg-primary-500/15 transition-all duration-300 shadow-lg"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500/0 via-primary-500/10 to-primary-500/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-primary-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 border border-primary-300">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                  {language === 'en' ? 'Our Mission' : 'مهمتنا'}
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed">
                  {language === 'en'
                    ? 'To provide fast, reliable, and transparent delivery services that exceed customer expectations while supporting the growth of businesses across the UAE. We strive to be the bridge that connects businesses with their customers seamlessly.'
                    : 'تقديم خدمات توصيل سريعة وموثوقة وشفافة تتجاوز توقعات العملاء مع دعم نمو الشركات في جميع أنحاء الإمارات. نسعى جاهدين لأن نكون الجسر الذي يربط الشركات بعملائها بسلاسة.'}
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative bg-primary-500/10 backdrop-blur-md rounded-2xl p-8 border border-primary-200 hover:bg-primary-500/15 transition-all duration-300 shadow-lg"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500/0 via-primary-500/10 to-primary-500/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-primary-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 border border-primary-300">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                  {language === 'en' ? 'Our Vision' : 'رؤيتنا'}
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed">
                  {language === 'en'
                    ? 'To become the leading delivery service provider in the UAE, known for innovation, reliability, and exceptional customer service. We envision a future where every delivery is effortless, transparent, and exceeds expectations.'
                    : 'أن نصبح مزود خدمة التوصيل الرائد في الإمارات، معروفين بالابتكار والموثوقية وخدمة العملاء الاستثنائية. نتخيل مستقبلاً حيث يكون كل توصيل بلا جهد وشفاف ويتجاوز التوقعات.'}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Smart Mile Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
              {language === 'en' ? 'Why Smart Mile' : 'لماذا سمارت مايل'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'What Sets Us Apart' : 'ما يميزنا'}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              {language === 'en'
                ? 'Discover the unique advantages that make Smart Mile your ideal delivery partner'
                : 'اكتشف المزايا الفريدة التي تجعل سمارت مايل شريك التوصيل المثالي لك'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whySmartMile.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-6 border-2 border-primary-100 hover:border-primary-300 transition-all shadow-md hover:shadow-xl"
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
              {language === 'en' ? 'Why Choose Us' : 'لماذا تختارنا'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Your Trusted Delivery Partner' : 'شريك التوصيل الموثوق'}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              {language === 'en'
                ? 'Experience the difference that comes with choosing a delivery service built on trust, innovation, and excellence'
                : 'اختبر الفرق الذي يأتي مع اختيار خدمة توصيل مبنية على الثقة والابتكار والتميز'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all border-l-4 border-primary-500 hover:border-primary-600"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-4xl flex-shrink-0">{item.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {item.title}
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-base">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Advantages Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
              {language === 'en' ? 'Our Advantages' : 'مزايانا'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Competitive Edge' : 'الميزة التنافسية'}
            </h2>
          </div>
          
          <div className="space-y-8">
            {advantages.map((advantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-8`}
              >
                {/* Icon Circle */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-xl">
                      <span className="text-5xl md:text-6xl">{advantage.icon}</span>
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full border-4 border-white"></div>
                  </div>
                </div>

                {/* Content Card */}
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-left' : 'md:text-right'} text-center md:text-left w-full`}>
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border-t-4 border-primary-500 relative overflow-hidden group">
                    {/* Decorative gradient overlay */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-500 via-primary-400 to-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-2xl font-bold text-primary-600">0{index + 1}</span>
                        <div className="flex-1 h-px bg-gradient-to-r from-primary-200 to-transparent"></div>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                        {advantage.title}
                      </h3>
                      <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                        {advantage.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Experience Excellence Section */}
      <section className="py-20 md:py-32 bg-gradient-to-r from-primary-500 to-primary-700 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'en' ? 'Ready to Experience Excellence?' : 'هل أنت مستعد لتجربة التميز؟'}
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
              {language === 'en'
                ? 'Join thousands of satisfied customers who trust Smart Mile for their delivery needs'
                : 'انضم إلى آلاف العملاء الراضين الذين يثقون بسمارت مايل لاحتياجات التوصيل الخاصة بهم'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl"
              >
                {language === 'en' ? 'Get Started Today' : 'ابدأ اليوم'}
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 bg-white/20 backdrop-blur-md text-white border-2 border-white/30 rounded-lg font-semibold hover:bg-white/30 transition-all"
              >
                {language === 'en' ? 'View Services' : 'عرض الخدمات'}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default About
